/**
 * #2496 — where the CP auth server sends an identity it will not sign in.
 *
 * ⛔ ONE SENTENCE FOR FOUR BRANCHES, AND THE SAMENESS IS THE FEATURE (K2, D1). A wrong email
 * domain, a link whose operator row is gone, a link whose operator is disabled, and an address no
 * operator row matches all land here and read identically. Splitting them would hand an
 * unauthenticated caller on an internet-facing host an oracle for the platform's domain policy,
 * and the operator who needs the distinction has it on the `oauth_login_failed` audit row's
 * `reason`.
 *
 * ⛔ THE EXPECTED DOMAIN IS NEVER NAMED, here or anywhere the caller can read. The server used to
 * answer this branch with the configured allowlist joined into an exception message; naming it on
 * this page would re-leak the same fact through a different surface.
 *
 * ⛔ NO QUERY PARAMETER IS READ. A `?reason=` would be caller-chosen text rendered by a page we
 * serve, which deletes the property that makes the server's redirect table safe — every refusal
 * URL is `SITE_ADMIN_URL` plus a path literal the server owns, with no caller input in it.
 *
 * ⛔ AND NO RETRY. Retrying cannot help: the refusal is about who the person is, not about what
 * went wrong in transit. The one link goes back to `/login` so a different account can be used,
 * which is `AuthCardFrame`'s stated rule for a terminal card — never invent an action that does
 * nothing.
 */
import { AuthCardFrame } from '../shell/AuthCardFrame';
import { ButtonLink } from '../shell/ButtonLink';

export function SignInUnavailablePage(): React.JSX.Element {
  return (
    <AuthCardFrame
      title="You can’t sign in right now"
      /*
       * ⛔ FOCUS MOVES TO THE TITLE ON ARRIVAL (D-1), same rule as the sibling refusal page: this
       * card replaces the whole page after a redirect the person did not initiate.
       */
      autoFocusTitle
      notice={{
        /*
         * ⛔ `none`, the NEUTRAL verdict — not `bad`. A refused sign-in is the system doing what it
         * was told, which is the same reasoning `people-model.ts` records for rendering a `revoked`
         * account `muted` rather than as a fault. `bad` would also make this card assertive through
         * `NOTICE_ROLE`, announcing a policy outcome as an alarm.
         */
        verdict: 'none',
        message:
          'This account can’t be used to sign in to the Zion admin console. If you think that’s wrong, contact your administrator.',
      }}
      testId="sign-in-unavailable"
    >
      {/* A real anchor, not a `<button onClick={navigate}>`: `/login` has a URL, so right-click,
          middle-click and copy-link all keep working, and it announces as a link.

          ⛔ `default` tone, NOT `primary`. A primary button recommends the action it carries, and
          this page recommends nothing — signing in again with the same account fails the same way.
          The sibling `/sign-in-incomplete` DOES recommend its retry and is `primary` for that
          reason; the two differ here on purpose. */}
      <ButtonLink to="/login" size="md" data-testid="sign-in-unavailable-back">
        Back to sign in
      </ButtonLink>
    </AuthCardFrame>
  );
}
